import type { ReactNode } from 'react';
import { cn } from './cn.js';

/**
 * Dado que se LÊ — rótulo à esquerda, valor à direita, separados por um fio.
 *
 * ## O defeito que isto conserta, e ele é de honestidade
 *
 * "Minha conta" desenhava e-mail, CPF e telefone dentro de uma **caixa com
 * borda, rótulo pequeno em cima e valor em peso médio embaixo** — que é a
 * anatomia exata de um campo de formulário. Nada ali é editável, e a tela
 * compensava com um selo "Leitura" no alto do cartão: uma etiqueta explicando
 * uma mentira visual em vez de desfazê-la.
 *
 * Quem chega numa caixa dessas toca nela. Não acontece nada, e a pessoa não
 * conclui "é só leitura" — conclui que o aplicativo travou.
 *
 * ## E de coerência
 *
 * Os **mesmos três campos** apareciam no Início numa terceira forma: grade de
 * duas colunas, sem fio, sem caixa. Dois desenhos para o mesmo dado no mesmo
 * aplicativo, a dois toques de distância um do outro. Nenhum teste pega isso —
 * cada tela, sozinha, estava certa.
 *
 * A forma daqui é a que o `Evogard Portal v4` desenha na tela de conta:
 * `padding: 12px 0` com `border-top` de um pixel, rótulo em 12,5px de apoio e
 * valor em 13,5px peso 600, alinhados pela LINHA DE BASE — e não pelo centro,
 * senão um valor de duas linhas descola do próprio rótulo.
 *
 * O fio é `borda-suave`, o mesmo da separação entre linhas de tabela: ele
 * separa sem virar moldura. `borda` daria a cada linha o peso de um contorno, e
 * aí a lista volta a parecer uma pilha de caixas.
 */
export function DataList({ children, className }: { children: ReactNode; className?: string }) {
  return <dl className={cn('text-sm', className)}>{children}</dl>;
}

/**
 * Uma linha da lista.
 *
 * `<div>` envolvendo o par `<dt>/<dd>` é HTML válido desde a 5.2, e é o que
 * permite dar borda e distribuição ao par sem quebrar a relação que o leitor de
 * tela lê. A alternativa seria abandonar a `<dl>` — e aí rótulo e valor viram
 * dois textos soltos que por acaso estão perto.
 */
export function DataRow({ label, children }: { label: ReactNode; children: ReactNode }) {
  return (
    <div className="flex items-baseline justify-between gap-4 border-t border-borda-suave py-2.5">
      <dt className="shrink-0 text-texto-suave">{label}</dt>
      {/*
       * O valor QUEBRA, e não corta.
       *
       * A primeira versão levava `truncate`, e a primeira tela em que ela rodou
       * mostrou o custo: "PEUGEOT · 208 ACTIVE/ACTIVE PACK 1.5 FLEX 8V 5P ·
       * 2016" virou "PEUGEOT · 208 ACTIVE/ACTIVE PA…". Modelo de veículo é
       * justamente o que a pessoa dita ao telefone para a central — cortar ali
       * é perder o dado para ganhar uma linha.
       *
       * `min-w-0` continua, e agora por outro motivo: sem ele o item de flex
       * não encolhe abaixo do próprio conteúdo, e um valor longo empurraria o
       * rótulo para fora da tela em vez de quebrar dentro do espaço que tem.
       *
       * `wrap-anywhere` é a terceira metade da mesma decisão, e faltava.
       * "Quebra, não corta" só vale onde HÁ onde quebrar: um e-mail não tem
       * espaço nenhum, e com `overflow-wrap: normal` ele não quebra nem corta —
       * transborda o cartão em silêncio. Medido no navegador a 390px em
       * 10/09/2026, na tela "Minha conta":
       * `maria.aparecida.nascimento@construtoraexemplo.com.br` pedia 383px numa
       * caixa de 251, e os 132 de diferença saíam pela borda direita. O e-mail
       * é o valor mais comum desta lista, e o endereço que cabe hoje é o de uma
       * conta de teste.
       *
       * E é `wrap-anywhere`, não `break-words`: os dois quebram a palavra na
       * pintura, e só o primeiro conta a quebra no tamanho MÍNIMO do conteúdo.
       * Com `break-words` o `min-w-0` acima não tem efeito nenhum — o item de
       * flex se recusa a encolher abaixo dos 383px que a palavra inteira mede,
       * e a linha transborda igual. Foi o que a primeira correção fez, e ela
       * media exatamente o mesmo número.
       */}
      <dd className="min-w-0 text-right font-semibold wrap-anywhere">{children}</dd>
    </div>
  );
}
